import { BATCH_TRANSFER_ITEM_SPECIFICS_SCHEMA } from './batch-transfer-specifics';
import { BATCH_TRANSFER_CERTIFICATION_SCHEMA } from './batch-transfer-presentation';
import {
  PROFESSIONAL_GRADERS,
  PROFESSIONAL_GRADES,
  RAW_CARD_CONDITIONS,
} from './card-presentation';
import { EBAY_SPORT_CARD_FEATURES } from './ebay-seller-hub';
import { CHECKLIST_ISSUE_TYPES } from './checklist';
import {
  BATCH_TRANSFER_MAX_CARDS,
  BATCH_TRANSFER_SCHEMA_VERSION,
  BATCH_TRANSFER_CAPTURE_ISSUE_TYPES,
  BATCH_TRANSFER_IDENTITY_FIELDS,
  BATCH_TRANSFER_MAX_DESKEW_DEGREES,
  BATCH_TRANSFER_MIN_DESKEW_DEGREES,
  BATCH_TRANSFER_RESEARCH_FIELDS,
  BATCH_TRANSFER_RESULT_STATUSES,
  type BatchIdentificationResults,
  type BatchTransferInternalManifest,
} from './batch-transfer';
const string = (maxLength = 300, allowEmpty = false) => ({
  type: 'string',
  minLength: allowEmpty ? 0 : 1,
  maxLength,
  ...(allowEmpty ? {} : { pattern: '\\S' }),
});
const object = (properties: Record<string, unknown>, required: string[] = []) => ({
  type: 'object',
  additionalProperties: false,
  properties,
  required,
});
const uuid = { type: 'string', pattern: '^[a-fA-F0-9]{8}-(?:[a-fA-F0-9]{4}-){3}[a-fA-F0-9]{12}$' };
const identityProperties: Record<string, unknown> = Object.fromEntries(
  [
    'category',
    'set',
    'number',
    'name',
    'year',
    'brand',
    'subset',
    'team',
    'language',
    'variation',
  ].map((k) => [k, string()]),
);
identityProperties.attributes = string(1000);
for (const k of ['rookie', 'autograph', 'memorabilia', 'serialNumbered'])
  identityProperties[k] = { type: 'boolean' };
for (const k of ['serialTo', 'serialNumber'])
  identityProperties[k] = { type: 'integer', minimum: 1, maximum: 1000000 };
const identity = object(identityProperties, ['category', 'set', 'number', 'name']);
const confidence = { type: 'number', minimum: 0, maximum: 1 };
const evidence = { type: 'array', maxItems: 10, items: string(1000) };
const source = object(
  {
    sourceSystem: string(),
    sourceTargetId: string(),
    sourceItemId: string(),
    snapshotFingerprint: string(),
    parallelDefinitionId: string(),
  },
  ['sourceSystem', 'sourceTargetId', 'sourceItemId'],
);
const selection = object(
  {
    sourceSystem: string(),
    sourceTargetId: string(),
    sourceItemId: string(),
    sourceSubsetId: string(),
    sourceDefinitionType: { enum: ['SUBSET', 'PARALLEL'] },
    snapshotFingerprint: string(),
    sourceFamilyId: string(),
    checklistId: string(),
    subset: string(300, true),
    naturalKey: object(
      {
        year: string(),
        brand: string(300, true),
        product: string(300, true),
        number: string(),
        cardSet: string(),
        sourceItemId: string(),
      },
      ['year', 'brand', 'product', 'number'],
    ),
    parallel: object(
      {
        definitionId: string(),
        name: string(),
        sourceSubsetId: string(),
        snapshotFingerprint: string(),
      },
      ['definitionId', 'name', 'sourceSubsetId', 'snapshotFingerprint'],
    ),
  },
  [
    'sourceSystem',
    'sourceTargetId',
    'sourceItemId',
    'snapshotFingerprint',
    'checklistId',
    'subset',
    'naturalKey',
  ],
);
const amountMinor = { type: 'integer', minimum: 1, maximum: 100000000 };
const listing = {
  ...object({
    title: string(77),
    itemSpecifics: BATCH_TRANSFER_ITEM_SPECIFICS_SCHEMA,
    presentation: {
      oneOf: [
        object({ kind: { const: 'RAW' }, condition: { enum: RAW_CARD_CONDITIONS } }, [
          'kind',
          'condition',
        ]),
        object(
          {
            kind: { const: 'GRADED' },
            grader: { enum: PROFESSIONAL_GRADERS },
            grade: { type: 'number', enum: PROFESSIONAL_GRADES },
            certificationNumber: BATCH_TRANSFER_CERTIFICATION_SCHEMA,
          },
          ['kind', 'grader', 'grade'],
        ),
      ],
    },
    description: string(1000),
    suggestedPrice: object({ amountMinor, currency: { const: 'USD' } }, [
      'amountMinor',
      'currency',
    ]),
    priceBasis: string(2000),
    priceSources: {
      type: 'array',
      minItems: 1,
      maxItems: 10,
      items: {
        ...object(
          {
            url: {
              ...string(2000),
              format: 'uri',
              pattern: '^(?!https?://[^/?#]*@)https?://\\S+$',
            },
            kind: { enum: ['SOLD', 'ACTIVE', 'OTHER'] },
            amountMinor,
            currency: { const: 'USD' },
          },
          ['url', 'kind'],
        ),
        dependencies: { amountMinor: ['currency'] },
      },
    },
    features: {
      type: 'array',
      maxItems: EBAY_SPORT_CARD_FEATURES.length,
      uniqueItems: true,
      items: { enum: EBAY_SPORT_CARD_FEATURES },
    },
  }),
  minProperties: 1,
  dependencies: { suggestedPrice: ['priceBasis'], priceSources: ['suggestedPrice'] },
};
const notes = (key: string, choices: readonly string[], maxItems: number) => ({
  type: 'array',
  minItems: 1,
  maxItems,
  items: object({ [key]: { enum: choices }, note: string(1000) }, [key, 'note']),
});
// A side needs a quarter turn or a deskew of at least the minimum angle; smaller angles are rejected as noise.
const orientationSide = {
  ...object({
    quarterTurns: { enum: [0, 1, 2, 3] },
    deskewDegrees: {
      type: 'number',
      minimum: -BATCH_TRANSFER_MAX_DESKEW_DEGREES,
      maximum: BATCH_TRANSFER_MAX_DESKEW_DEGREES,
      not: {
        exclusiveMinimum: -BATCH_TRANSFER_MIN_DESKEW_DEGREES,
        exclusiveMaximum: BATCH_TRANSFER_MIN_DESKEW_DEGREES,
      },
    },
  }),
  anyOf: [
    { required: ['deskewDegrees'] },
    { properties: { quarterTurns: { enum: [1, 2, 3] } }, required: ['quarterTurns'] },
  ],
};
const imageOrientation = {
  ...object({ front: orientationSide, back: orientationSide }),
  minProperties: 1,
};
/** Portable JSON Schema; the server also validates duplicate IDs and serial semantics. */
export const BATCH_TRANSFER_RESULTS_SCHEMA: Record<string, unknown> = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'TCVS batch identification results v1/v2',
  ...object(
    {
      schemaVersion: { enum: [1, 2] },
      exportId: uuid,
      batchId: { type: 'string', pattern: '^tcvs:batch:[0-9A-HJKMNP-TV-Z]{26}$' },
      runId: string(100),
      producer: object({ tool: string(100), model: string(200), version: string(200) }, ['tool']),
      cards: {
        type: 'array',
        minItems: 1,
        maxItems: BATCH_TRANSFER_MAX_CARDS,
        items: {
          ...object(
            {
              cardId: uuid,
              inputFingerprint: { type: 'string', pattern: '^[a-f0-9]{64}$' },
              status: { enum: BATCH_TRANSFER_RESULT_STATUSES },
              identity,
              confidence,
              evidence,
              candidates: {
                type: 'array',
                maxItems: 15,
                items: object({ identity, confidence, evidence }, ['identity']),
              },
              checklistSource: source,
              checklistSourceSelection: selection,
              listing,
              draftDisagreements: notes('field', BATCH_TRANSFER_IDENTITY_FIELDS, 18),
              captureIssues: notes('type', BATCH_TRANSFER_CAPTURE_ISSUE_TYPES, 10),
              catalogIssues: notes('type', CHECKLIST_ISSUE_TYPES, 10),
              imageOrientation,
            },
            ['cardId', 'inputFingerprint', 'status'],
          ),
          allOf: [
            {
              if: { properties: { status: { const: 'MATCHED' } } },
              then: { required: ['identity'] },
            },
            { not: { required: ['checklistSource', 'checklistSourceSelection'] } },
          ],
        },
      },
    },
    ['schemaVersion', 'exportId', 'batchId', 'runId', 'producer', 'cards'],
  ),
  allOf: [
    {
      if: { properties: { schemaVersion: { const: 1 } } },
      then: {
        properties: {
          cards: {
            items: {
              not: {
                anyOf: BATCH_TRANSFER_RESEARCH_FIELDS.map((field) => ({ required: [field] })),
              },
            },
          },
        },
      },
    },
  ],
};
export function buildBatchIdentificationResultsTemplate(
  manifest: BatchTransferInternalManifest,
): BatchIdentificationResults {
  return {
    schemaVersion: BATCH_TRANSFER_SCHEMA_VERSION,
    exportId: manifest.exportId,
    batchId: manifest.batchId,
    runId: 'replace-with-unique-run-id',
    producer: { tool: 'Claude Desktop' },
    cards: manifest.cards.map((c) => ({
      cardId: c.cardId,
      inputFingerprint: c.inputFingerprint,
      status: 'NOT_PROCESSED',
    })),
  };
}
